import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useNavigationStore } from '../stores/navigation'
import CustomerLogos from '@/components/common/CustomerLogos'
import GlobalImpactPartnershipsSection from '@/components/ui/GlobalImpactPartnershipsSection'

const About: React.FC = () => {
  const { t } = useTranslation()
  const { openDemoModal } = useNavigationStore()

  return (
    <div className="min-h-screen w-full text-slate-900">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-100 bg-gradient-to-br from-indigo-600 via-sky-500 to-emerald-400"/>
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-3xl"/>
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-white/10 blur-3xl"/>
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-2xl font-bold bg-gradient-to-r from-emerald-300 to-sky-400 bg-clip-text text-transparent uppercase tracking-wide sm:text-3xl">
              {t('about.hero.title')}
            </p>
            <h1 className="mt-4 text-5xl font-extrabold leading-tight text-white sm:text-6xl">
              {t('about.hero.heading')}
          </h1>
            <p className="mt-3 max-w-2xl text-lg text-white/90">
              {t('about.hero.subtitle')}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#overview" className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow hover:shadow-md transition">
                {t('about.hero.companyOverview')}
              </a>
              <Link to="/service-summary" className="rounded-xl bg-white/10 px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-white/15 transition">
                {t('about.hero.ourPlatform')}
              </Link>
              <button 
                onClick={openDemoModal}
                className="rounded-xl bg-emerald-900/20 px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-emerald-900/30 transition"
              >
                {t('common.bookDemo')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section id="overview" className="bg-white scroll-mt-20">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
          <div className="grid items-start gap-10 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t('about.companyOverview.title')}
              </h2>
              <p className="mt-3 text-slate-600">
                {t('about.companyOverview.subtitle')}
              </p>
            </div>
            <div className="lg:col-span-2 grid gap-6 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  {t('about.companyOverview.whoWeAre.title')}
                </h3>
                <p className="mt-2 text-slate-600">
                  {t('about.companyOverview.whoWeAre.description')}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  {t('about.companyOverview.whereWeOperate.title')}
                </h3>
                <p className="mt-2 text-slate-600">
                  {t('about.companyOverview.whereWeOperate.description')}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  {t('about.companyOverview.whoWeServe.title')}
                </h3>
                <p className="mt-2 text-slate-600">
                  {t('about.companyOverview.whoWeServe.description')}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  {t('about.companyOverview.securityCompliance.title')}
                </h3>
                <p className="mt-2 text-slate-600">
                  {t('about.companyOverview.securityCompliance.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Streamlined Client Logos */}
      <section className="relative overflow-hidden bg-gradient-to-tr from-slate-200 via-slate-300 to-slate-200">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <p className="text-center text-sm font-semibold uppercase tracking-wider text-slate-700">
            {t('about.trustedBy.title')}
          </p>
          <div className="mt-8">
            <CustomerLogos 
              logoUids={["dfci", "rady", "bcehs", "nhs"]} 
              className="grid grid-cols-2 gap-6 sm:grid-cols-4 justify-items-center"
            />
          </div>
        </div>
      </section>

      {/* The Eceptionist Difference */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:py-14">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">
              {t('about.difference.title')}
            </h2>
            <p className="mt-2 text-slate-600">
              {t('about.difference.subtitle')}
            </p>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { key: 'expertise', title: t('about.difference.expertise.title'), desc: t('about.difference.expertise.description') },
              { key: 'innovation', title: t('about.difference.innovation.title'), desc: t('about.difference.innovation.description') },
              { key: 'results', title: t('about.difference.results.title'), desc: t('about.difference.results.description') },
              { key: 'scalability', title: t('about.difference.scalability.title'), desc: t('about.difference.scalability.description') },
            ].map((item) => (
              <div key={item.key} className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-1 rounded-full bg-gradient-to-b from-indigo-500 to-emerald-500"/>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-100 bg-gradient-to-tr from-indigo-50 via-sky-50 to-emerald-50"/>
        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                {t('about.impact.title')}
              </h2>
              <p className="mt-3 text-slate-600">
                {t('about.impact.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { kpi: '25', label: t('about.impact.yearsInOperation') },
                { kpi: '6', label: t('about.impact.globalRegionsServed') },
                { kpi: '1,000+', label: t('about.impact.organizationsSupported') },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                  <div className="text-3xl font-extrabold text-slate-900">{s.kpi}</div>
                  <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GlobalImpactPartnershipsSection />

      {/* Platform (Concise) */}
      <section id="platform" className="bg-white scroll-mt-20">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:py-14">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">
              {t('about.platform.title')}
            </h2>
            <p className="mt-2 text-slate-600">
              {t('about.platform.description1')}
            </p>
            <p className="mt-2 text-slate-600">
              {t('about.platform.description2')}
            </p>
            <p className="mt-2 text-slate-600">
              {t('about.platform.description3')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story / Timeline */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-100 bg-gradient-to-tr from-emerald-50 via-sky-50 to-indigo-50"/>
        <div className="relative mx-auto max-w-7xl px-6 py-12 lg:py-14">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">
              {t('about.story.title')}
            </h2>
            <p className="mt-2 text-slate-600">
              {t('about.story.subtitle')}
            </p>
          </div>

          {/* Horizontal Timeline */}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="relative">
              {/* Timeline rail */}
              <div className="absolute left-0 right-0 top-8 h-1 rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500"/>

              {/* Milestones */}
              <div className="flex items-start justify-between gap-6 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none]">
                {Object.entries(t('about.story.milestones', { returnObjects: true }) as Record<string, string>).map(([year, text]) => (
                  <div key={year} className="min-w-[130px] flex-1">
                    <div className="flex flex-col items-center text-center">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{year}</div>
                      <div className="mt-3 h-4 w-4 rounded-full bg-gradient-to-br from-indigo-500 to-emerald-500 ring-4 ring-white shadow"/>
                      <div className="mt-3 text-sm text-slate-700">{text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-100 bg-gradient-to-br from-indigo-600 via-sky-500 to-emerald-500"/>
        <div className="relative mx-auto max-w-7xl px-6 py-16">
          <div className="mx-auto max-w-3xl text-center text-white">
            <h2 className="text-3xl font-bold sm:text-4xl">
              {t('about.cta.title')}
            </h2>
            <p className="mt-3 text-white/90">
              {t('about.cta.subtitle')}
            </p>
            <div className="mt-8">
              <button 
                onClick={openDemoModal}
                className="inline-flex items-center justify-center rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow hover:shadow-md transition"
              >
                {t('common.bookDemo')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Page Footer Accent (visual polish only) */}
      <div className="h-2 w-full bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500"/>
    </div>
  )
}

export default About


